import React from "react";
import { CircleMarker, Marker, Popup, Tooltip } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "../../redux/hooks";
import { busAtStop, busIconBlue } from "../../utils/mapIcons";
import { positionAccuracy } from "../../config";
import { dynamicTextColor } from "../../utils/helpers";

//* Styled-Components - Tooltip - with props
export const StyledTooltip = styled.div<{ $selected?: boolean; $randomColor?: string }>`
  border: ${(props) => (props.$selected ? "2px solid black" : "none")};
  font-size: ${(props) => (props.$selected ? "140%" : "110%")};
  padding: ${(props) => (props.$selected ? "3px" : "2px")};
  background-color: ${(props) => props.$randomColor || "initial"};
  color: ${(props) => dynamicTextColor(props.$randomColor || "initial")};
  font-weight: 700;
`;

const Last24HoursReportMap = (): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [last24HoursPositionsFromRedux, randomColorsArrayFromRedux, vehicleId]: [Last24HoursPositions[], string[], string] =
    useAppSelector(
      (state: RootState) => [
        state?.last24HoursPositions?.positions,
        state?.last24HoursPositions?.colorsArray,
        state?.last24HoursPositions?.vehicleId,
      ],
      shallowEqual
    );
  // console.log("last24HoursPositionsFromRedux:", last24HoursPositionsFromRedux);

  const [last24HoursPositions, setLast24HoursPositions] = React.useState<Last24HoursPositions[] | null>(null);
  // console.log("last24HoursPositions:", last24HoursPositions);

  React.useEffect(() => {
    if (last24HoursPositionsFromRedux && randomColorsArrayFromRedux) {
      const last24HoursPositionsArray = [] as Last24HoursPositions[];
      for (let i = 0; i < last24HoursPositionsFromRedux.length; i++) {
        for (let j = 0; j < randomColorsArrayFromRedux.length; j++) {
          if (i === j) {
            const positionObject = {
              lat: last24HoursPositionsFromRedux[i].lat,
              lon: last24HoursPositionsFromRedux[i].lon,
              maxTime: last24HoursPositionsFromRedux[i].maxTime,
              vehicleId: last24HoursPositionsFromRedux[i].vehicleId,
              randomColor: randomColorsArrayFromRedux[j],
              name: last24HoursPositionsFromRedux[i].name || "",
            } as Last24HoursPositions;
            last24HoursPositionsArray.push(positionObject);
          }
        }
      }
      setLast24HoursPositions(last24HoursPositionsArray);
    }
  }, [last24HoursPositionsFromRedux, randomColorsArrayFromRedux]);

  const BusesIcons = (): JSX.Element => {
    const busesIcons =
      last24HoursPositions &&
      last24HoursPositions?.map((elem: Last24HoursPositions, index: number) => (
        <Marker
          key={elem.vehicleId + index}
          position={{
            lat: elem.lat,
            lng: elem.lon,
          }}
          icon={vehicleId === elem.vehicleId ? busIconBlue : busAtStop}
        >
          <Tooltip permanent={true} direction={index % 2 === 0 ? "right" : "left"}>
            <StyledTooltip $selected={vehicleId === elem.vehicleId ? true : false} $randomColor={elem.randomColor}>
              {elem.name || elem?.vehicleId || `${t("b/d")}`}
            </StyledTooltip>
          </Tooltip>

          <Popup>
            {t("Id pojazdu")}:{" "}
            <span className="span_bold">
              {elem?.name ? `${elem?.name} (${elem?.vehicleId})` : elem?.vehicleId || t("b/d")}
            </span>
            <br />
            {t("Czas ostatniego odczytu GPS")}: <span className="span_bold">{elem?.maxTime?.split(".")?.[0]}</span>
            <br />
            {t("Pozycja")}:{" "}
            <span className="span_bold">{`${elem?.lat.toFixed(positionAccuracy)}, ${elem?.lon.toFixed(
              positionAccuracy
            )}`}</span>
          </Popup>
          {pathname.includes("/last24hours") && vehicleId === elem.vehicleId ? (
            <CircleMarker
              radius={25}
              pathOptions={{
                stroke: true,
                color: elem.randomColor,
                weight: 4,
                opacity: 0.85,
                fillColor: elem.randomColor,
                fillOpacity: 0.3,
              }}
              center={{
                lat: elem.lat,
                lng: elem.lon,
              }}
            />
          ) : null}
        </Marker>
      ));
    return <React.Fragment>{busesIcons}</React.Fragment>;
  };
  return <React.Fragment>{BusesIcons()}</React.Fragment>;
};

export default Last24HoursReportMap;
